<script setup>
import { ref, onBeforeUnmount } from 'vue'

import FormButton from '@/components/Atoms/FormButton.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import Badge from '@/components/Badge.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import IconCheckmark from '@/components/icon/IconCheckmark.vue'

const props = defineProps({
	count: { type: Number, default: 0 },
	disabled: { type: Boolean, default: false },
	disabledTooltip: { type: String, default: '' },
	label: { type: String, default: 'Add' },
	successLabel: { type: String, default: 'Added' },
	successDuration: { type: Number, default: 2000 },
	buttonType: { type: String, default: 'secondary' },
	buttonSize: { type: String, default: 'sm' },
	showPlusIcon: { type: Boolean, default: true },
	// Optional async/sync handler. Return `false` to skip the success state.
	// When set, used instead of the `add` emit for performing the action.
	action: { type: Function, default: null },
})

const emit = defineEmits(['add'])

const showSuccess = ref(false)
const pending = ref(false)
let successTimer = null

const clearSuccessTimer = () => {
	if (successTimer) {
		clearTimeout(successTimer)
		successTimer = null
	}
}

const showSuccessFeedback = () => {
	showSuccess.value = true
	clearSuccessTimer()
	successTimer = setTimeout(() => {
		showSuccess.value = false
		successTimer = null
	}, props.successDuration)
}

const onAdd = async () => {
	if (props.disabled || showSuccess.value || pending.value) return
	pending.value = true
	try {
		if (props.action) {
			const result = await props.action()
			if (result === false) return
		} else {
			emit('add')
		}
		showSuccessFeedback()
	} finally {
		pending.value = false
	}
}

onBeforeUnmount(clearSuccessTimer)
</script>

<template>
	<div class="inline-flex items-center gap-2 shrink-0">
		<Transition name="add-badge">
			<Badge
				v-if="count > 0"
				type="blue"
				size="xs"
				pill
				inline
				class="!h-5 !w-5 !min-w-5 !p-0 justify-center tabular-nums border border-white box-border"
				:title="String(count)"
			/>
		</Transition>

		<div class="inline-flex items-center h-7">
			<Transition name="add-feedback" mode="out-in">
				<div
					v-if="showSuccess"
					key="success"
					class="inline-flex items-center gap-2 text-tiny text-success"
					role="status"
				>
					<icon-base :width="12" :height="12" icon-color="currentColor">
						<icon-checkmark />
					</icon-base>
					{{ successLabel }}
				</div>
				<Tooltip
					v-else
					key="button"
					wrapperClass="inline-flex shrink-0"
					:content="disabledTooltip"
					:active="disabled && !!disabledTooltip"
				>
					<FormButton
						:type="buttonType"
						:size="buttonSize"
						:disabled="disabled || pending"
						@click="onAdd"
					>
						<icon-base v-if="showPlusIcon" class="mr-2">
							<icon-plus />
						</icon-base>
						<slot>{{ label }}</slot>
					</FormButton>
				</Tooltip>
			</Transition>
		</div>
	</div>
</template>

<style scoped>
.add-feedback-enter-active,
.add-feedback-leave-active {
	transition: opacity 180ms ease, transform 180ms ease;
}

.add-feedback-enter-from,
.add-feedback-leave-to {
	opacity: 0;
	transform: translateY(2px);
}

.add-badge-enter-active {
	transition: opacity 200ms ease, transform 200ms ease;
}

.add-badge-leave-active {
	transition: opacity 150ms ease, transform 150ms ease;
}

.add-badge-enter-from {
	opacity: 0;
	transform: scale(0.7);
}

.add-badge-leave-to {
	opacity: 0;
	transform: scale(0.85);
}
</style>
